// New MobX-driven Video Interview UI wired to stores, leaving legacy component untouched.
import { observer } from "mobx-react-lite";
import { useStores } from "../../stores/rootStore";
import useUser from "../auth/useUser";
import { Link, useParams } from "react-router";
import { useEffect, useMemo, useRef } from "react";
import StreamingMic from "../Mic";
import AudioVisualizer from "../AudioVisualizer";
import LargeLoader from "../LargeLoader";
import { Box, Button, Typography, IconButton, Tooltip, Alert, LinearProgress, FormControl, FormLabel, Input, Select, Option } from "@mui/joy";
import LogViewer from "./LogViewer";
import { useFeedback } from "../feedback/Service";
import { useTheme } from '@mui/joy/styles';
import { useMediaQuery } from '@mui/material';
import { REALTIME_VOICE_OPTIONS } from "../../services/realtime.service";
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import useUnsavedChangesGuard from "../useUnsavedChangesGuard";

const AUDIO_MOTION_CONTAINER_ID = "audio-motion-container-new";

const RecordingTimer = observer(function RecordingTimer() {
  const { interview } = useStores();
  const elapsedSec = interview.elapsedSec;
  const minutes = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
  const seconds = String(elapsedSec % 60).padStart(2, '0');
  return <Typography level="body-md" sx={{ mt: 1, fontWeight: 'bold' }}>Recording Time: {minutes}:{seconds}</Typography>;
});

const SectionStatusBar = observer(function SectionStatusBar() {
  const { interview } = useStores();
  const section = interview.currentSection;
  if (!section) return null;
  const total = Number(section.durationSec || 0);
  const elapsed = Number(interview.sectionElapsedSec || 0);
  const pct = total > 0 ? Math.min(100, (elapsed / Math.max(1, total)) * 100) : null;
  const wrapSoon = Boolean(interview.wrapSoon);
  const over = total > 0 && elapsed > total;
  const fmt = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(Math.max(0, Math.floor(s % 60))).padStart(2, '0');
    return `${m}:${ss}`;
  };
  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography level="body-sm" sx={{ fontWeight: 600 }}>Current section: {section.name || '—'}</Typography>
        {total > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="body-xs" sx={{ opacity: 0.8 }}>{fmt(elapsed)} / {fmt(total)}{over ? ` (+${fmt(elapsed - total)})` : ''}</Typography>
            {wrapSoon && !over && (
              <Typography level="body-xs" color="warning" sx={{ fontWeight: 600 }}>Wrapping soon…</Typography>
            )}
            {over && (
              <Typography level="body-xs" color="danger" sx={{ fontWeight: 600 }}>Over time</Typography>
            )}
          </Box>
        )}
      </Box>
      {pct != null && (
        <LinearProgress
          determinate
          value={pct}
          variant="soft"
          color={over ? 'danger' : (wrapSoon ? 'warning' : 'primary')}
        />
      )}
    </Box>
  );
});

const VideoInterview = observer(function VideoInterview() {
  const { user, refetch } = useUser();
  const { jobId, clientId } = useParams();
  const { interview, recorder: recorderStore, realtime, uploader } = useStores();
  const { logStore } = useStores();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDemo = user?.client?._id === clientId;
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const audioViz = useRef(null);
  const feedback = useFeedback();
  useUnsavedChangesGuard(interview.state !== 'Finalized', feedback.confirm);

  const pdfStatus = useMemo(() => user?.talent?.pdfStatus, [user]);

  // Resolve application like legacy component
  const application = useMemo(() => {
    if (!user) return null;
    return user.talent?.applications?.find(a => a.job._id === jobId && a.client._id === clientId) || null;
  }, [user, jobId, clientId]);

  const micHealthy = recorderStore.micHealthy;
  const cameraReady = recorderStore.cameraReady;

  // Hook remote track into recorder and visualizer
  useEffect(() => {
    const handler = (stream) => {
      try { recorderStore.mixRemoteAudio(stream); } catch (e) { void e; }
      if (audioViz.current) {
        audioViz.current.start(stream);
      }
    };
    realtime.service.on('remoteTrack', handler);
    return () => realtime.service.off('remoteTrack', handler);
  }, [realtime.service, recorderStore]);

  // Initialize audio visualizer container lazily
  useEffect(() => {
    if (audioViz.current) return;
    const init = () => {
      if (audioViz.current) {
        return;
      }
      const container = document.getElementById(AUDIO_MOTION_CONTAINER_ID);
      if (!container || !container.clientHeight) {
        setTimeout(init, 300);
        return;
      }
      audioViz.current = new AudioVisualizer({ containerId: AUDIO_MOTION_CONTAINER_ID, height: container.clientHeight || 300 });
      audioViz.current.init();
    };
    init();
    return () => { try { audioViz.current?.destroy(); } catch (e) { void e; } audioViz.current = null; };
  }, []);

  const onMicConnected = () => {
    if (!videoRef.current) return;
    // Populate store stream from underlying Recorder instance
    try { if (recorderStore.recorder.stream) recorderStore.setStream(recorderStore.recorder.stream); } catch (e) { void e; }
    videoRef.current.srcObject = recorderStore.recorder.stream || recorderStore.stream;
    const markReady = () => {
      const v = videoRef.current;
      // console.log("[VideoInterview] markReady - checking video readiness", v?.readyState, v?.videoWidth);
      if (v && v.videoWidth > 0) recorderStore.setCameraReady(true);
    };
    // console.log("[VideoInterview] onMicConnected - checking video readiness", videoRef.current.readyState, videoRef.current.videoWidth);
    if (videoRef.current.readyState >= 2 && videoRef.current.videoWidth > 0) {
      recorderStore.setCameraReady(true);
    } else {
      videoRef.current.onloadedmetadata = markReady;
      videoRef.current.oncanplay = markReady;
    }
  };

  const handleStart = async () => {
    if (!application?._id) return;
    try {
      await interview.start(application._id, { audioEl: audioRef.current });
    } catch (error) {
      console.error("[VideoInterview] Error starting interview", error);
      // Mirror legacy behavior: show a user-visible error when server start fails
      try {
        const message = error?.message || "Error starting recorder";
        feedback.snackbar({ text: message, type: "error" });
      } catch (e) { void e; }
    }
  };

  const handleMute = () => {
    recorderStore.isMuted ? recorderStore.unmute() : recorderStore.mute();
  };

  const handleOutputVoiceChange = (_, value) => {
    if (!value) return;
    realtime.setOutputConfig({ voice: value });
  };

  const handleOutputSpeedChange = (event) => {
    realtime.setOutputConfig({ speed: event.target.value });
  };

  const askToFinish = async () => {
    try {
      const res = await feedback.confirm({
        title: "Finish Interview?",
        text: "Are you sure you want to finish the interview? You will not be able to record more answers.",
      });
      if (!res) return;
      await interview.askToFinish({ force: true, isByUser: true });
    } catch (e) { void e; }
  };
  const goToNextSection = () => interview.nextSection({ forceImmediate: false });

  const handleTestRelay = async () => {
    feedback.snackbar({ text: "Testing relay connection…", type: "info" });
    const res = await realtime.testRelay();
    feedback.snackbar({
      text: res.ok ? `Relay OK — ${res.detail}` : `Relay FAILED — ${res.detail}`,
      type: res.ok ? "success" : "error",
    });
  };

  const handleSimulateAzureError = (persistent) => {
    const sent = realtime.simulateAzureError({ persistent });
    if (!sent) {
      feedback.snackbar({ text: "Wait until the interviewer is idle", type: "warning" });
      return;
    }
    feedback.snackbar({
      text: persistent ? "Sent malformed request (expect retries to exhaust)" : "Sent malformed request (expect retry to recover)",
      type: "info",
    });
  };

  // Attach uploader when recording id is known
  useEffect(() => {
    if (!interview.recordingId) return;
    uploader.attach(interview.recordingId);
  }, [interview.recordingId, uploader]);

  // Stream chunks from recorder to uploader
  useEffect(() => {
    const onChunk = (blob) => {
      try { uploader.enqueue(blob); } catch (e) { void e; }
    };
    recorderStore.recorder.on('videochunk', onChunk);
    return () => recorderStore.recorder.off('videochunk', onChunk);
  }, [recorderStore, uploader]);

  // Keep polling for pdfStatus until 'ready', mirroring legacy behavior
  useEffect(() => {
    if (pdfStatus === 'ready') return undefined;
    const t = setInterval(() => refetch(), 5000);
    return () => clearInterval(t);
  }, [pdfStatus, refetch]);

  const isInProgress = interview.state === 'In Progress';
  const isSaving = interview.state === 'Finalizing' || interview.state === 'Finalized';
  // console.log("[VideoInterview] render - interview state:", interview.state);
  const canStart = (interview.state === 'Idle' || interview.state === 'Error') && micHealthy && cameraReady;

  if (!user) return <LargeLoader />;
  if (pdfStatus !== 'ready') {
    return <LargeLoader text={`Please wait while we prepare your interview. This may take a few minutes. Current state: ${pdfStatus}`} />;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 0 } }}>
      <Typography level="h2" sx={{ mt: { xs: 5, md: 0 }, mb: { xs: 5, md: 2 }, fontSize: { xs: 'xl', sm: 'xl2', md: 'xl3' }, textAlign: { xs: 'center', md: 'left' } }}>Video Interview (New)</Typography>
      {interview.state === 'Starting' && <Typography level="body-sm">Initializing interview...</Typography>}
      <Box sx={{ display: isSaving ? 'none' : undefined }}>
        <Box sx={{ mt: 0, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {micHealthy ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <CancelOutlinedIcon color="warning" fontSize="small" />}
            <Typography level="body-sm">Mic {micHealthy ? 'ready' : 'input not detected'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {cameraReady ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <CancelOutlinedIcon color="warning" fontSize="small" />}
            <Typography level="body-sm">Camera {cameraReady ? 'ready' : 'not ready'}</Typography>
          </Box>
        </Box>
        <StreamingMic
          recorder={recorderStore.recorder}
          onMicConnected={onMicConnected}
          isMuted={false}
          onLevelChange={level => recorderStore.setMicLevel(level)}
        />
        {logStore.devMode && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-end',
              flexWrap: 'wrap'
            }}
          >
            <Typography level="title-sm" sx={{ width: '100%' }}>Realtime output</Typography>
            <FormControl size="sm" sx={{ minWidth: 180 }}>
              <FormLabel>Voice</FormLabel>
              <Select value={realtime.outputVoice} onChange={handleOutputVoiceChange}>
                {REALTIME_VOICE_OPTIONS.map((voice) => (
                  <Option key={voice} value={voice}>{voice}</Option>
                ))}
              </Select>
            </FormControl>
            <FormControl size="sm" sx={{ width: 120 }}>
              <FormLabel>Speed</FormLabel>
              <Input
                type="number"
                value={realtime.outputSpeed}
                onChange={handleOutputSpeedChange}
                slotProps={{ input: { min: 0.25, max: 1.5, step: 0.05 } }}
              />
            </FormControl>
            <Button size="sm" variant="soft" color="neutral" onClick={handleTestRelay}>
              Test relay
            </Button>
            <Button size="sm" variant="soft" color="warning" onClick={() => handleSimulateAzureError(false)}>
              Simulate Azure error → recover
            </Button>
            <Button size="sm" variant="soft" color="danger" onClick={() => handleSimulateAzureError(true)}>
              Simulate Azure error → exhaust
            </Button>
          </Box>
        )}
        <div
          id={AUDIO_MOTION_CONTAINER_ID}
          style={{
            width: '100%',
            aspectRatio: isMobile ? (isSmallMobile ? '1/1' : '4/3') : '16/9',
            position: 'relative',
            minHeight: isMobile ? (isSmallMobile ? '250px' : '300px') : '400px',
            maxHeight: '70vh',
            marginTop: isMobile ? 8 : 12
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: realtime.connectionState === 'Connected'
                ? { xs: '50%', sm: '40%', md: '30%' }
                : '100%',
              transition: 'width 0.5s ease-in-out'
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              style={{
                width: '100%',
                transform: 'scaleX(-1)',
                WebkitTransform: 'scaleX(-1)',
                borderRadius: isMobile ? '8px' : '4px'
              }}
            />
          </Box>
          {realtime.captions && realtime.connectionState === 'Connected' && (
            <Typography
              level="body-sm"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: 'rgba(255,255,255,0.3)',
                backdropFilter: 'blur(3px)',
                p: { xs: 1, sm: 1.5, md: 2 },
                width: '100%',
                textAlign: 'center',
                zIndex: 1300,
                fontSize: { xs: 'sm', sm: 'sm', md: 'sm' },
                fontWeight: 500,
                borderRadius: isMobile ? '8px 8px 0 0' : '4px 4px 0 0',
                pb: { xs: 0, sm: 0, md: 0 }
              }}
            >
              {realtime.captions}
            </Typography>
          )}
        </div>
        <SectionStatusBar />
        <audio ref={audioRef} autoPlay crossOrigin="anonymous" />
        <Box sx={{ textAlign: 'center', mt: { xs: 1, sm: 1.5, md: 2 }, position: 'relative', width: '100%' }}>
          <Tooltip 
            title={recorderStore.isAutoMuted 
              ? 'Mic muted during intro' 
              : (recorderStore.isMuted ? 'Enable Microphone' : 'Mute Microphone')} 
            variant='outlined'
          >
            <span>
              <IconButton 
                onClick={handleMute} 
                variant="outlined" 
                color={recorderStore.isMuted ? 'danger' : 'primary'} 
                size="md" 
                disabled={recorderStore.isAutoMuted}
                sx={{ position: 'absolute', top: -6, right: 8, boxShadow: 'sm' }}
              >
                {recorderStore.isMuted ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {recorderStore.isAutoMuted && (
            <Typography 
              level="body-sm" 
              color="warning" 
              sx={{ 
                mt: 0.5, 
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5
              }}
            >
              <MicOffIcon fontSize="small" /> Your mic is muted during the section intro
            </Typography>
          )}
          {isInProgress && <RecordingTimer />}
          <Typography level="body-md" sx={{ mt: 1, fontSize: { xs: 'sm', md: 'md' } }}>
            Recorder: {recorderStore.recorderState} | Connection: {realtime.connectionState} | Interview: {interview.state}
          </Typography>
          {!isInProgress && (
            <Button variant="solid" color="primary" disabled={!canStart} onClick={handleStart} sx={{ mt: 2, minWidth: 200 }}>
              {interview.state === 'Error' ? 'Retry start' : 'Start interview'}
            </Button>
          )}
          {isInProgress && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
              {logStore.devMode &&
                <Button variant="soft" color="neutral" onClick={goToNextSection}>
                  Next section
                </Button>
              }
              <Button variant="outlined" color="danger" onClick={askToFinish}>
                Ask to Finish
              </Button>
            </Box>
          )}
          {!micHealthy && <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>Mic input not detected. Please check your device settings.</Typography>}
        </Box>
      </Box>
      <Box sx={{ display: isSaving ? undefined : 'none', px: { xs: 1, sm: 2, md: 0 } }}>
        {interview.state === 'Finalizing' && (
          <Box>
            <Alert
              sx={{
                alignItems: "flex-start",
                gap: { xs: 1, md: 2 },
                mt: { xs: 1, md: 2 },
                flexDirection: { xs: 'column', sm: 'row' }
              }}
              startDecorator={<WarningIcon />}
              variant="soft"
              color="warning"
            >
              <div>
                <div>Important Notice!</div>
                <Typography level="body-sm" color="warning" sx={{ fontSize: { xs: 'xs', sm: 'sm' } }}>
                  Please stay on this page until the recording is fully saved. Closing, refreshing, or navigating away may result in data loss.
                </Typography>
              </div>
            </Alert>
            {uploader.percent !== null && (
              <Typography
                level="title-lg"
                sx={{
                  mt: { xs: 1.5, md: 2 },
                  fontSize: { xs: 'lg', sm: 'xl', md: 'xl2' },
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Saving state: {uploader.percent < 100 ? `Uploading... ${uploader.percent.toFixed(1)}%` : "Saving..."}
              </Typography>
            )}
          </Box>
        )}
        {interview.state === 'Finalized' && (
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              sx={{
                mt: { xs: 1.5, md: 2 },
                fontSize: { xs: 'lg', sm: 'xl', md: 'xl2' }
              }}
              level="title-md"
            >
              Recording saved successfully!
            </Typography>
            <Typography
              sx={{
                mt: { xs: 1.5, md: 2 },
                fontSize: { xs: 'sm', sm: 'md' },
                lineHeight: { xs: 1.4, md: 1.5 }
              }}
              level="body-md"
            >
              All done! Thanks for completing your interview. You can now close this window — the company will be in touch with next steps if it&apos;s a good match.
            </Typography>
            {isDemo && (
              <Typography
                sx={{
                  mt: { xs: 1.5, md: 2 },
                  fontSize: { xs: 'sm', sm: 'md' },
                  fontWeight: 'bold'
                }}
                level="body-md"
              >
                Demo interview completed. You&apos;ll see a summary of your performance in your job candidates list in 5-7 minutes.
              </Typography>
            )}
            <Button
              variant="solid"
              color="primary"
              size={isMobile ? "md" : "lg"}
              sx={{
                mt: { xs: 2, md: 3 },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '200px' }
              }}
              component={Link}
              to={isDemo ? '/jobs' : '/applications'}
            >
              Back to {isDemo ? "Jobs" : "Applications"}
            </Button>
          </Box>
        )}
      </Box>
      {logStore.devMode && <LogViewer />}
    </Box>
  );
});

export default VideoInterview;
